/**
 * Payments vs the ledger (iter 612).
 *
 * Every payment the platform takes should have exactly one row in the
 * authoritative ledger. This lists what does not reconcile — a payment that
 * never posted, two payments sharing one reference, a ledger row whose source
 * record is gone — so it is seen here rather than at an audit.
 */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, CheckCircle2, Copy, GitPullRequestClosed, Loader2, RefreshCw, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (amount, currency) =>
  `${currency || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`.trim();

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border bg-slate-50 p-3" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-semibold text-slate-800">{value}</p>
  </div>
);

export const ReconciliationPanel = () => {
  const { token } = useAuthStore();
  const [days, setDays] = useState('90');

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['finance-reconciliation', days],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/finance/reconciliation?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not run the reconciliation');
      return res.json();
    },
    enabled: !!token,
  });

  const post = useMutation({
    mutationFn: async (entries) => {
      const res = await fetch(`${API_URL}/api/admin/finance/reconciliation/post`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Could not post');
      return res.json();
    },
    onSuccess: (result) => {
      toast.success(
        `${result.posted_count} payment${result.posted_count === 1 ? '' : 's'} posted to the ledger`
        + (result.refused_count ? ` · ${result.refused_count} refused` : ''),
      );
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const totals = data?.totals || {};

  return (
    <Card data-testid="reconciliation-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <GitPullRequestClosed className="h-5 w-5 text-indigo-500" />
            Payments vs the ledger
          </CardTitle>
          <CardDescription>
            Money that exists in one place and not the other
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]" data-testid="reconciliation-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
              <SelectItem value="730">Last two years</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}
                  data-testid="reconciliation-refresh">
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {data?.clean && (
          <p className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800"
             data-testid="reconciliation-clean">
            <CheckCircle2 className="h-4 w-4" />
            Every payment in this window has exactly one ledger entry
            ({data.checked} checked).
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Checked" value={data?.checked ?? '—'} testid="recon-checked" />
          <Stat label="Not in the ledger" value={totals.unposted ?? '—'} testid="recon-unposted" />
          <Stat label="Duplicate refs" value={totals.duplicates ?? '—'} testid="recon-duplicates" />
          <Stat label="Value unposted" value={money(totals.unposted_value, '')} testid="recon-value" />
        </div>

        {(data?.unposted || []).length > 0 && (
          <div className="space-y-2" data-testid="recon-unposted-table">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              Payments with no ledger entry
            </h4>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Posting replays each one through the same code that records
                money today — pressing it twice still posts once.
              </p>
              <Button size="sm" variant="secondary"
                      disabled={post.isPending}
                      data-testid="post-all-to-ledger"
                      onClick={() => post.mutate(
                        data.unposted.map((r) => ({ source: r.source, id: r.id })))}>
                {post.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <Upload className="mr-2 h-4 w-4" />}
                Post all {data.unposted.length} to the ledger
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>What</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="text-right">Fix</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.unposted.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell><Badge variant="outline">{row.source}</Badge></TableCell>
                    <TableCell className="capitalize">{row.what || '—'}</TableCell>
                    <TableCell className="max-w-[220px] truncate">{row.unit || '—'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {money(row.amount, row.currency)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {(row.at || '').slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" disabled={post.isPending}
                              data-testid={`post-to-ledger-${row.id}`}
                              onClick={() => post.mutate([{ source: row.source, id: row.id }])}>
                        Post
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {(data?.duplicates || []).length > 0 && (
          <div className="space-y-2" data-testid="recon-duplicates-table">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Copy className="h-4 w-4 text-rose-500" />
              Two payments sharing one reference
            </h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.duplicates.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell><Badge variant="outline">{row.source}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{row.reference}</TableCell>
                    <TableCell className="text-right">{money(row.amount, '')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {(data?.orphans || []).length > 0 && (
          <div className="space-y-2" data-testid="recon-orphans-table">
            <h4 className="text-sm font-semibold text-slate-700">
              Ledger entries whose source record is gone
            </h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.orphans.map((row) => (
                  <TableRow key={row.ledger_id}>
                    <TableCell><Badge variant="outline">{row.source}</Badge></TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell className="text-right">{money(row.amount, row.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReconciliationPanel;
